<template>
    <div
        ref="cardElement"
        class="flex-fill"
        :class="{
            'card-transparent': isTransparent,
            'card-tabs': internalTabs !== undefined && internalTabs.Tabs.length > 0,
            'card border shadow-sm h-100': !(hideTabsOnOnlyOne && (internalTabs === undefined || internalTabs.Tabs.length == 1)),
        }"
        v-if="!destroyed"
    >
        <div
            :class="[
                getHeaderBackgroundColor(),
                {
                    'card-header py-1 d-flex flex-nowrap align-items-center justify-content-between d-inline-block': !(
                        hideTabsOnOnlyOne &&
                        (internalTabs === undefined || internalTabs.Tabs.length == 1)
                    ),
                },
            ]"
        >
            <!-- Show normal header -->
            <template v-if="internalTabs?.Tabs?.length === 1">
                <span
                    class="text-truncate py-1"
                    v-if="!hideTabsOnOnlyOne"
                    ><IconComp
                        v-if="internalTabs?.Tabs[0].Icon != undefined"
                        :icon="internalTabs?.Tabs[0].Icon!"
                        class="me-2"
                    />{{ $tm(internalTabs?.Tabs[0].TranslationKey!) }}</span
                >
            </template>

            <!-- Show header as tabs -->
            <template v-else-if="internalTabs !== undefined && internalTabs.Tabs.length > 0">
                <ul
                    class="nav nav-tabs flex-fill"
                    role="tablist"
                >
                    <li
                        class="nav-item me-3 display-5"
                        role="presentation"
                        v-for="(tab, i) in internalTabs?.Tabs"
                        :key="i"
                        :class="{ 'ms-auto': tab.Alignment == 'right' }"
                    >
                        <a
                            class="nav-link d-flex flex-row pe-0"
                            :class="{ active: internalTabs.isTabActive(tab) }"
                            :id="getTabID(tab, i)"
                            :data-bs-target="'#' + getTabPaneID(tab, i)"
                            :aria-selected="internalTabs.isTabActive(tab) ? true : undefined"
                            :aria-controls="getTabPaneID(tab, i)"
                            type="button"
                            role="tab"
                            v-on="{ 'shown.bs.tab': shown }"
                            @click.capture="setActiveTab(getTabID(tab, i))"
                            ><IconComp
                                v-if="tab.Icon !== undefined"
                                :icon="tab.Icon!"
                                class="me-2"
                            />
                            <span class="flex-fill pe-4"
                                >{{ $t(tab.TranslationKey, tab.TranslationKey)
                                }}<small
                                    v-if="tab.Badgetext"
                                    class="badge text-white small rounded-pill ms-3"
                                    :class="tab.BadgeColor ? 'bg-' + tab.BadgeColor : 'bg-dark'"
                                    >{{ tab.Badgetext }}</small
                                ></span
                            >
                            <span
                                class="alert alert-warning m-0 p-0 px-1 display-6 mt-1 me-1"
                                :style="{ height: '1.2rem !important', width: '1.2rem !important' }"
                                v-if="tab.HasWarning === true"
                                ><IconComp :icon="featureIcons.WARNING"
                            /></span>
                            <span
                                class="alert alert-danger m-0 p-0 px-1 display-6 mt-1 me-1"
                                :style="{ height: '1.2rem !important', width: '1.2rem !important' }"
                                v-if="tab.HasError === true"
                                ><IconComp :icon="featureIcons.ERROR"
                            /></span>

                            <ButtonComp
                                v-if="props.pinable !== false"
                                button-type="Custom"
                                class="pin btn-sm p-0 m-0 px-2 no-focus"
                                :icon="isPinnedTabs.has(tab.TranslationKey!) ? 'fa-thumbtack-slash' : 'fa-thumbtack'"
                                @click="pinCurrentTab(tab, tab.Icon)"
                            ></ButtonComp>
                        </a>
                    </li>
                </ul>
            </template>

            <!-- Show the buttons on the right side of the header -->
            <div
                class="flex-shrink-1 py-1 d-flex align-items-stretch gap-1"
                v-if="!(hideTabsOnOnlyOne && (internalTabs === undefined || internalTabs.Tabs.length == 1))"
            >
                <slot name="buttons"></slot>
                <ButtonComp
                    v-if="internalTabs?.Tabs?.length === 1 && props.pinable !== false"
                    :button-type="getButtonType()"
                    :icon="isPinnedTabs.has(internalTabs.Tabs[0]?.TranslationKey) ? 'fa-thumbtack-slash' : 'fa-thumbtack'"
                    @click="pinCurrentTab(internalTabs?.Tabs[0], internalTabs?.Tabs[0].Icon!)"
                >
                </ButtonComp>
                <ButtonComp
                    v-if="props.resize !== false"
                    :button-type="getButtonType()"
                    :icon="!isMinimized ? 'fa-window-minimize' : 'fa-window-maximize'"
                    @click="isMinimized = !isMinimized"
                >
                </ButtonComp>
                <ButtonComp
                    v-if="props.detach !== false"
                    :button-type="getButtonType()"
                    icon="fa-arrow-up-right-from-square"
                    @click="detachComp()"
                >
                </ButtonComp>
                <ButtonComp
                    v-if="props.close !== false"
                    :button-type="getButtonType()"
                    icon="fa-xmark"
                    @click="destroyed = !destroyed"
                />
            </div>
        </div>
        <div
            class="card-body"
            :class="[isMinimized ? 'd-none' : '', contentFill ? 'content-fill' : '', overflow ? '' : '', props.tabBodyClasses ?? '']"
        >
            <!-- Create tab slots if we are using headers  -->
            <template v-if="internalTabs !== undefined && internalTabs.Tabs.length > 0">
                <div
                    class="tab-pane fade h-100"
                    :class="{ 'show active': internalTabs?.isTabActive(tab), 'overflow-auto': props.overflow }"
                    :id="getTabPaneID(tab, i)"
                    :aria-labelledby="getTabID(tab, i)"
                    role="tabpanel"
                    tabindex="-1"
                    v-for="(tab, i) in internalTabs?.Tabs"
                    :key="i"
                >
                    <slot :name="`tab-${tab.Name}`"></slot>
                </div>
            </template>

            <!-- Create the default slot if we are not using tabs -->
            <slot
                v-else
                name="default"
            ></slot>

            <LoadingComp
                v-if="loadState?.AllDone === false"
                :state="loadState"
            />
        </div>
    </div>
</template>

<script lang="ts" setup>
import { ButtonComp, DisplayTime, IconComp, LoadingComp } from '@/components';
import type { ButtonTypes } from '@/components/Basic/ButtonComp.vue';
import { BackgroundColors } from '@/components/consts/colors';
import { useHelper } from '@/composeables';
import type { TaskLoaderState } from '@/composeables/loaders';
import type { TabContainer, TabDefinition } from '@/objects/Tabs';
import { type ITabContainer } from '@/objects/Tabs';
import { onActivated, onMounted, ref, watch } from 'vue';
import { usePinnedViews, usePinnedViewsRef } from '@/composeables/pinnedviews/pinnedViews';
import { Router } from '@/objects/Router';
import type { Route } from '@/objects/Route';
import type { IconName, StackedIcon } from '@/fontawesome';
import { featureIcons } from '../Menu/featureicons';

const props = withDefaults(
    defineProps<{
        /**
         * If we want to show tabs in the header instead of a title
         * @default undefined
         */
        tabs: TabContainer;

        /**
         * If the content should expand or get a scrollbar
         * @default false
         */
        overflow?: boolean;

        /**
         * If the card should be minimizable and maximizable
         * @default false
         */
        resize?: boolean;

        /**
         * If the card should be closable
         * @default false
         */
        close?: boolean;

        /**
         * The color of the header
         * @default 'Tertiary'
         */
        color?: ButtonTypes;

        /**
         * If the card should be transparent
         * @default false
         */
        isTransparent?: boolean;

        /**
         * If the content should fill the card
         * @default false
         */
        contentFill?: boolean;

        /**
         * If we should be able to open the card in a new detached browser window
         * @default false
         */
        detach?: boolean;

        /**
         * The state of the loader. Will show a loader instead of content
         * @default undefined
         */
        loadState?: TaskLoaderState;

        /**
         * If the card should be pinable to the quick access bar
         * @default true
         */
        pinable?: boolean;

        /**
         * If there is only one tab, we hide the tabs and header and it will just be a container
         * @default false
         */
        hideTabsOnOnlyOne?: boolean;

        /**
         * Additional classes to add to the tab body (the content area)
         */
        tabBodyClasses?: string;
    }>(),
    {
        resize: false,
        close: false,
        detach: false,
        isTransparent: false,
        contentFill: false,
        pinable: true,
        hideTabsOnOnlyOne: false,
        overflow: false,
    },
);

const cardElement = ref<HTMLDivElement | null>(null);

// We need to know if ths TabComp is in a modal or not
let isInAModal: boolean = false;

// The internal state of the card component
const internalTabs = ref<TabContainer>(props.tabs);

// Keeps track if it should be minimized or not
const isMinimized = ref<boolean>(false);

// Keeps track if it should be destroyed or not Destroyed, means it doesnt get rendered in the DOM
const destroyed = ref<boolean>(false);

// We need a Map to know the actual href for each tab
const tabHrefMap = new Map<string, Route | null>();

/**
 * Helper function to get the background color of the header
 */
const getHeaderBackgroundColor = (): string => BackgroundColors.getColor(props.color ?? 'Teritary') + '-subtle';

/**
 * Helper function to get the button type for the buttons in the header
 */
const getButtonType = (): ButtonTypes => {
    if ((props.color ?? 'Tertiary') === 'Tertiary') {
        return 'Secondary';
    }

    return props.color as ButtonTypes;
};

/**
 * Helper function to detach the card component.
 * We open the URL in a new minimal browserwindow with the fullscreen query parameter
 */
const detachComp = () => {
    window.open(window.location.href + '?fullscreen=true', '_blank', 'toolbar=0,location=0,menubar=0');
};

/**
 * Callback for when it is shown, then we set the focus to the first element in the card
 */
const shown = (e?: Event) => useHelper().setFocus();

/**
 * This is basically the same code as it is in the Tablayout component, so we should try and reuse it
 * Create a random GUID since bootstrap is dependent on unique ID attributes to work
 * @param tab
 * @param index
 */
const getTabID = (tab: TabDefinition, index: number) => `${tab.Name}`;

/**
 * Helper function to get the tab pane ID
 * @param tab
 * @param index
 */
const getTabPaneID = (tab: TabDefinition, index: number) => `${getTabID(tab, 0)}-pane`;

/**
 * We set the active tab when we press a tab
 * @param tabName
 */
const setActiveTab = (tabName: string) => {
    const oldTab = internalTabs.value.GetActiveTab;
    internalTabs.value.SetActiveTab(tabName);
    emit('TabShown', oldTab, internalTabs.value.GetActiveTab);
};

/**
 * Handle pinning of the view
 */
const { ActivePinnedViews } = usePinnedViewsRef();
const { PinView, UnpinView } = usePinnedViews();

// A list of the current pinned tabs in this card
// TODO: We might not need the actual object here, just the keys
const isPinnedTabs = ref<Set<string>>(new Set());

/**
 * Pin the current tab
 * @param item The tab to pin. If no tab is provided, the default view is pinned
 */
const pinCurrentTab = (item: TabDefinition, icon?: IconName | StackedIcon) => {
    // TODO: It is a bit anoying that we have to check if the item is a string or a TabDefinition
    // We should always use tabs in this component, and create another component if we need boxed content like on the focus page
    // But for now we need to support both
    const key: string = item.TranslationKey;
    const hash: string | undefined = '';

    const route = tabHrefMap.get(key);

    if (route === undefined) {
        return;
    }

    if (isPinnedTabs.value.has(key)) {
        UnpinView(key, hash);
        isPinnedTabs.value.delete(key);
    } else {
        PinView(key, route!, icon);
        isPinnedTabs.value.add(key);
    }
};

/**
 * Set the pinned state for the tabs or the default view
 */
const setIsPinned = () => {
    internalTabs.value?.Tabs.forEach((tab) => {
        const route = Router.CurrentRoute;
        const params = route.params ?? {};

        if (isInAModal) {
            params.subtab = tab.Name;
        } else {
            params.tab = tab.Name;
            params.subtab = undefined;
            params.id = undefined;
        }

        const viewId = `${route.name}${JSON.stringify(params)}undefined`;

        if (ActivePinnedViews.value.has(viewId)) {
            isPinnedTabs.value.add(tab.TranslationKey);
        } else {
            isPinnedTabs.value.delete(tab.TranslationKey);
        }
    });
};

/**
 * Watch the pinned views to see if this view is pinned or not
 */
watch(
    ActivePinnedViews,
    () => {
        setIsPinned();
    },
    { deep: true },
);

onMounted(() => {
    // We need to know if we are located in a modal or not. We use this to know if we are using :subtab or :tab
    isInAModal = !!cardElement.value?.closest('.modal-body');

    // We loop through the tabs and try and create a map with the translationkey and the href
    internalTabs.value?.Tabs.forEach((tab) => {
        const params = Router.RouteParams;

        if (isInAModal) {
            params.subtab = tab.Name;
        } else {
            params.tab = tab.Name;
            params.subtab = undefined;
            params.id = undefined;
        }

        tabHrefMap.set(tab.TranslationKey, Router.GetRouteFromNameAndParams(Router.CurrentRoute?.name ?? '', params));
    });

    // Set the pinned state
    setIsPinned();

    // Set the focus to the first element in the card
    shown();
});

// We set the internal state of the card component to active or not
onActivated(() => {
    setIsPinned();
});

const emit = defineEmits<{
    (e: 'TabShown', oldTab: TabDefinition, newTab: TabDefinition): void;
}>();
</script>
